import { useTranslation } from 'react-i18next'
import { TemplateVariableBinding } from 'src/services/whatsapp'
import { bindingSourceEnum } from 'src/services/whatsapp/models'
import { humanize, isCheckinSurveyLink } from 'src/services/whatsapp/bindings'

interface TemplateVariablePromptsProps {
  promptedVars: string[]
  bindings: Record<string, TemplateVariableBinding>
  overrides: Record<string, string>
  setOverride: (name: string, value: string) => void
  disabled?: boolean
  /** Prefixes every test id, so each sender keeps its own (`composer`, ...). */
  testIdPrefix: string
}

/**
 * Inputs for the template variables the sender has to fill: Manual ones, and
 * bindings that could not resolve from the recipient's linked data.
 */
export const TemplateVariablePrompts = ({
  promptedVars,
  bindings,
  overrides,
  setOverride,
  disabled = false,
  testIdPrefix,
}: TemplateVariablePromptsProps) => {
  const { t } = useTranslation()
  if (!promptedVars.length) return null

  return (
    <div className="flex flex-wrap gap-3" data-testid={`${testIdPrefix}-template-prompts`}>
      {promptedVars.map((name) =>
        // The survey link can't be typed in: a reservation whose property
        // doesn't report to TRA/SIRE simply can't send it.
        isCheckinSurveyLink(bindings[name]) ? (
          <p
            key={name}
            className="text-sm text-error-600"
            data-testid={`${testIdPrefix}-var-unavailable-${name}`}
          >
            {humanize(name)}: {t('chat.surveyLinkUnavailable')}
          </p>
        ) : (
          <div key={name} className="flex min-w-48 flex-1 flex-col gap-1">
            <label
              htmlFor={`${testIdPrefix}-template-var-${name}`}
              className="text-sm font-medium text-primary"
            >
              {humanize(name)}
            </label>
            <input
              id={`${testIdPrefix}-template-var-${name}`}
              type="text"
              value={overrides[name] ?? ''}
              onChange={(e) => setOverride(name, e.target.value)}
              disabled={disabled}
              data-testid={`${testIdPrefix}-template-var-${name}`}
              className="w-full rounded-md border bg-surface px-3 py-2 text-text-primary placeholder:font-thin placeholder:text-text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            />
            {bindings[name] && bindings[name].source !== bindingSourceEnum.MANUAL && (
              <p
                className="text-xs text-text-tertiary"
                data-testid={`${testIdPrefix}-var-hint-${name}`}
              >
                {t('chat.bindingUnresolvedHint')}
              </p>
            )}
          </div>
        ),
      )}
    </div>
  )
}
